import {useEffect, useState} from "react";

import VLFModal from "@/components/Modal";
import MainActions from "@/components/MainActions";
import StatusBadge from "@/components/StatusBadge";
import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFDatePicker from "@/components/Input/DatePicker";
import {RosterAPI} from "@/api/roster";
import {
	convocationMessage,
	convocationPlayer,
	loadConvocationInfo,
	saveConvocationInfo,
	staffMember,
} from "@/utils/convocation";
import {generateConvocationPDF} from "@/utils/generateConvocationPDF";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";

// Convocazione per le famiglie: ritrovo, divisa e staff, poi PDF o messaggio
export default function ConvocationSheet({
	open,
	close,
	matchData,
	side,
	rosterId,
	clubName,
	teamName,
	players,
}) {
	const [info, setInfo] = useState(() =>
		loadConvocationInfo(matchData, side, rosterId),
	);
	const [staff, setStaff] = useState([]);
	const [feedback, setFeedback] = useState(null);

	useEffect(() => {
		if (!open) return;
		setInfo(loadConvocationInfo(matchData, side, rosterId));
		setFeedback(null);
		if (!rosterId) {
			setStaff([]);
			return;
		}
		let active = true;
		RosterAPI.getById(rosterId)
			.then((roster) => {
				if (active)
					setStaff((roster?.staff || []).map(staffMember).filter((s) => s.id));
			})
			.catch(() => active && setStaff([]));
		return () => {
			active = false;
		};
	}, [open, matchData?.id, side, rosterId]); // eslint-disable-line react-hooks/exhaustive-deps

	const staffOptions = staff.map((s) => ({
		value: s.id,
		label: s.role ? `${s.name} · ${s.role}` : s.name,
	}));
	const chosenStaff = staff.filter((s) => info.staffIds.includes(s.id));
	const sheet = {
		match: matchData,
		clubName,
		teamName,
		info,
		players: players.map(convocationPlayer),
		staff: chosenStaff,
	};

	const update = (key, value) => setInfo((prev) => ({...prev, [key]: value}));

	const downloadPDF = () => {
		saveConvocationInfo(matchData, side, rosterId, info);
		generateConvocationPDF(sheet);
	};

	const copyMessage = async () => {
		saveConvocationInfo(matchData, side, rosterId, info);
		try {
			await navigator.clipboard.writeText(convocationMessage(sheet));
			setFeedback("copied");
		} catch {
			setFeedback("manual");
		}
	};

	return (
		<VLFModal
			open={open}
			close={close}
			title="Convocazione"
			maxWidth="md"
			confirm={downloadPDF}
			confirmLabel="Scarica PDF"
			cancelLabel="Chiudi"
			disabled={!players.length}
			content={
				<div className="cv-sheet">
					<div className="cv-sheet__summary">
						{players.length ? (
							<StatusBadge tone="good" label={`${players.length} convocati`} />
						) : (
							<StatusBadge
								tone="warning"
								label="Nessun convocato: segnali prima nella lista"
							/>
						)}
					</div>
					<div className="row">
						<div className="col-sm-12 col-md-6">
							<VLFDatePicker
								type="datetime"
								label="Ritrovo"
								value={info.meetingAt}
								onChange={(value) => update("meetingAt", value)}
								clearable
							/>
						</div>
						<div className="col-sm-12 col-md-6">
							<VLFTextField
								label="Luogo di ritrovo"
								value={info.meetingPlace || ""}
								onChange={(e) => update("meetingPlace", e.target.value)}
								size="small"
							/>
						</div>
						<div className="col-sm-12 col-md-6">
							<VLFTextField
								label="Divisa"
								placeholder="Es. maglia blu, pantaloncini bianchi"
								value={info.kit || ""}
								onChange={(e) => update("kit", e.target.value)}
								size="small"
							/>
						</div>
						<div className="col-sm-12 col-md-6">
							<VLFSelectNew
								label="Staff"
								isMulti
								sorted={false}
								options={staffOptions}
								value={staffOptions.filter((o) =>
									chosenStaff.some((s) => s.id === o.value),
								)}
								onChange={(values) =>
									update(
										"staffIds",
										(values || []).map((v) => v.value),
									)
								}
								placeholder={
									staff.length ? "Staff" : "Nessuno staff nella rosa"
								}
							/>
						</div>
						<div className="col-sm-12">
							<VLFTextField
								label="Note"
								placeholder="Es. portare documento e borraccia"
								value={info.notes || ""}
								onChange={(e) => update("notes", e.target.value)}
								multiline
								minRows={2}
								maxRows={6}
								size="small"
							/>
						</div>
					</div>
					<div className="main-actions-bar">
						<MainActions
							actions={[
								{
									icon: <ContentCopyIcon />,
									label: "Copia messaggio",
									variant: "outlined",
									disabled: !players.length,
									onClick: copyMessage,
								},
							]}
						/>
						{feedback === "copied" && (
							<span className="cv-sheet__feedback">
								Messaggio copiato: incollalo nel gruppo delle famiglie
							</span>
						)}
					</div>
					{feedback === "manual" && (
						<VLFTextField
							label="Messaggio da copiare"
							value={convocationMessage(sheet)}
							onChange={() => {}}
							multiline
							minRows={6}
							maxRows={16}
							readOnly
							size="small"
						/>
					)}
				</div>
			}
		/>
	);
}
